/**
 * The legend a mental map is drawn from. It is the palette: the only shapes,
 * colours and lines on offer are the ones whose meaning is stated here.
 */

import { useState } from 'react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { ShapeGlyph } from './MapCanvas';
import { LINK_LEGEND, SHAPE_LEGEND, TONE_LEGEND, type MapShapeKind } from './model';

const EYEBROW = 'font-mono text-xs uppercase tracking-wider text-[var(--text-dim)]';

// ── Legend ───────────────────────────────────────────────────────────────────

export function Legend({ addShape, dragType }: { addShape: (kind: MapShapeKind) => void; dragType: string }) {
  const [open, setOpen] = useState(true);
  if (!open) {
    return (
      <div className="border-r border-[var(--rule)] bg-[var(--bg-raised)] p-2">
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)} aria-label="Show the legend" title="Show the legend">
          <PanelLeftOpen className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  return (
    <aside className="flex w-72 shrink-0 flex-col overflow-y-auto border-r border-[var(--rule)] bg-[var(--bg-raised)]">
      <div className="flex items-center justify-between px-4 pt-4">
        <h2 className="font-heading text-base font-semibold text-[var(--text-pri)]">Legend</h2>
        <Button onClick={() => setOpen(false)} aria-label="Hide the legend" title="Hide the legend" variant="ghost" size="sm" className="!p-1">
          <PanelLeftClose className="h-4 w-4" />
        </Button>
      </div>

      <section className="px-4 pt-4">
        <p className={EYEBROW}>Shapes — what kind of thing</p>
        <p className="mt-1 font-body text-xs text-[var(--text-dim)]">
          Click one to add it, or drag it onto your map. Hold Shift and drag across the map to select several at once.
        </p>
        <ul className="mt-2 space-y-1">
          {SHAPE_LEGEND.map((s) => (
            <li key={s.kind}>
              <Button
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(dragType, s.kind);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => addShape(s.kind)}
                variant="ghost" size="sm" className="w-full cursor-grab !justify-start !gap-3 rounded-md !px-2 !py-1.5 text-left !font-normal"
              >
                {s.kind === 'note' ? (
                  <span aria-hidden="true" className="w-8 shrink-0 text-center font-serif text-lg text-[var(--text-sec)]">
                    Aa
                  </span>
                ) : (
                  <ShapeGlyph
                    kind={s.kind}
                    color={TONE_LEGEND[0].color}
                    width={s.kind === 'layer' || s.kind === 'phase' ? 44 : 32}
                    height={s.kind === 'layer' ? 16 : s.kind === 'phase' ? 22 : 32}
                  />
                )}
                <span className="min-w-0">
                  <span className="block font-heading text-sm font-semibold text-[var(--text-pri)]">{s.name}</span>
                  <span className="block font-body text-xs text-[var(--text-sec)]">{s.meaning}</span>
                </span>
              </Button>
            </li>
          ))}
        </ul>
      </section>

      <section className="px-4 pt-5">
        <p className={EYEBROW}>Colour — how it stands</p>
        <p className="mt-1 font-body text-xs text-[var(--text-dim)]">Select a shape to change its colour or size.</p>
        <ul className="mt-2 space-y-1.5">
          {TONE_LEGEND.map((t) => (
            <li key={t.tone} className="flex items-center gap-3 px-2">
              <span className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: t.color }} />
              <span className="font-body text-sm text-[var(--text-pri)]">{t.meaning}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="px-4 pb-5 pt-5">
        <p className={EYEBROW}>Lines — how things relate</p>
        <p className="mt-1 font-body text-xs text-[var(--text-dim)]">
          Select a shape, then drag from one of its dots to another shape. Select a line to change what it means, or to write on it.
        </p>
        <ul className="mt-2 space-y-1.5">
          {LINK_LEGEND.map((l) => (
            <li key={l.kind} className="flex items-center gap-3 px-2">
              <svg width="36" height="10" aria-hidden="true" className="shrink-0">
                <line x1="1" y1="5" x2={l.arrow ? 28 : 35} y2="5" stroke={l.color} strokeWidth="2" strokeDasharray={l.dashed ? '5 4' : undefined} />
                {l.arrow && <polygon points="27,1 35,5 27,9" fill={l.color} />}
              </svg>
              <span className="min-w-0">
                <span className="block font-heading text-sm font-semibold text-[var(--text-pri)]">{l.name}</span>
                <span className="block font-body text-xs text-[var(--text-sec)]">{l.meaning}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
}
